import React, { FC, useEffect } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Modal from '@material-ui/core/Modal';

import colors from '../../../../themes/main-colors';
import {
  OutageLookupResponse,
  AccountDetail,
} from '../../../../__generated__/pge-types';
import {
  NotificationRequestAccountParams,
  OutageDetailInfo,
} from '../../OutageCenterPage';
import useGTMUtil from '../../../../lib/useGTMUtil';

import { Shadow } from '../../../../constants';
import OutageLocationLookup from './OutageLocationLookup';
import { useOutageEvents } from '../../useOutageEvents';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modalWrapper: {
      display: 'flex',
      width: '693px',
      maxHeight: '77.21vh',
      flexDirection: 'column',
      position: 'absolute',
      top: '50%',
      left: '50%',
      overflow: 'auto',
      transform: 'translate(-50%, -50%)',
      padding: '20px',
      backgroundColor: colors.modalBackgroundColor,
      borderRadius: '5px',
      [theme.breakpoints.down('sm')]: {
        top: '50%',
        width: '90%',
        maxHeight: '90%',
        padding: theme.spacing(3),
      },
    },
    closeButton: {
      position: 'absolute',
      top: 6,
      right: 24,
    },
    title: {
      lineHeight: '2.25rem',
    },
    checkBreakerCloseButton: {
      fontSize: '1rem',
      borderRadius: 5,
      color: colors.white,
      left: '50%',
      width: '168px',
      height: '38px',
      transform: 'translate(-50%)',
      background: colors.orange,
      letterSpacing: 0.32,
      boxShadow: `0px 5px 5px ${colors.boxShadowColor}`,
      '&:hover': {
        boxShadow: theme.shadows[5],
        background: colors.orange,
      },
    },
    buttonContainer: {
      display: 'flex',
      padding: '1rem',
      width: '100%',
      justifyContent: 'space-evenly',
    },
    backButton: {
      flex: 1,
    },
    subButtonContainer: {
      flex: '0',
    },
    reportOutageAccInfo: {
      background: colors.white,
      width: '100%',
      margin: '0px',
    },

    reportOutageContentStyle: {
      display: 'flex',
      flexDirection: 'column',
      boxShadow: Shadow.ONE,
    },
    reportOutageHeaderStyle: {
      display: 'flex',
      flexDirection: 'row',
      marginBottom: '1rem',
    },
    switchDetails: {
      display: 'flex',
      justifyContent: 'space-evenly',
      alignItems: 'center',
      marginLeft: 'auto',
    },
    modalBody: {},
    kickerText: {
      paddingBottom: theme.spacing(1.875),
      paddingTop: theme.spacing(1.25),
      fontSize: '1.25rem',
      lineHeight: '1.5rem',
      [theme.breakpoints.down('sm')]: {
        fontSize: '1rem',
      },
    },
    lowerCase: {
      textTransform: 'lowercase',
    },
    reportOutageAccInfoInner: {
      padding: '15px',
    },
    lookupByPhoneOrAcc: {
      display: 'flex',
      flexDirection: 'row',
    },

    phoneNumberCheckBox: {
      flexDirection: 'row',
      background: '#FFFCD9',
      display: 'flex',
      alignItems: 'center',
      padding: '0px 12px',
    },
  }),
);

type Props = {
  open: boolean;
  premiseInfo?: string;
  accontNumber: string;
  name: string;
  primaryContactNumber: string;
  onClose: (e?: any) => void;
  isOtherLocation: boolean;
  setShowReconnectPopup?: () => void;
  onPrevious: (hNumber?: string, showResetInfo?: boolean) => void;
  outageConfirmed?: () => void;
  setHouseNumber?: (houseNumber: string) => void;
  reportInfo: (lookupinfo: OutageDetailInfo) => void;
  reportOutageForLoggedIn: () => void;
  outageLookUpResult: (
    data?: OutageLookupResponse,
    isMultiPremise?: boolean,
    showSuccess?: boolean,
    showConnectionInfo?: boolean,
  ) => void;
  setLoggedInUserNotificationRequestAccountParams: React.Dispatch<
    React.SetStateAction<NotificationRequestAccountParams>
  >;
};

const OutageReportPopup: FC<Props> = ({
  open,
  onClose,
  onPrevious,
  premiseInfo,
  accontNumber,
  name,
  isOtherLocation,
  setShowReconnectPopup,
  primaryContactNumber,
  outageLookUpResult,
  setLoggedInUserNotificationRequestAccountParams,
  outageConfirmed,
  setHouseNumber,
  reportInfo,
  reportOutageForLoggedIn,
}: Props) => {
  const classes = useStyles();
  const { postGTMModalVisibilityData } = useGTMUtil();
  const { outageData, loading, accountDetails } = useOutageEvents();
  //Push modal visibility status to the GTM
  useEffect(() => {
    const GTMPageData = {
      page: 'Report Outage',
      siteSection: 'Report Outage Modal',
    };

    postGTMModalVisibilityData({ ...GTMPageData, isVisible: true });

    return () =>
      postGTMModalVisibilityData({ ...GTMPageData, isVisible: false });
  }, []);

  return (
    <Modal
      aria-labelledby="report-outage-modal"
      aria-describedby="report-outage-modal"
      open={open}
      onClose={onClose}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
    >
      <div className={classes.modalWrapper}>
        <OutageLocationLookup
          onPrevious={onPrevious}
          reportInfo={reportInfo}
          onClose={onClose}
          premiseInfo={premiseInfo}
          name={name}
          isOtherLocation={isOtherLocation}
          setShowReconnectPopup={setShowReconnectPopup}
          primaryContactNumber={primaryContactNumber}
          accontNumber={accontNumber}
          setHouseNumber={setHouseNumber}
          outageLookUpResult={outageLookUpResult}
          reportOutageForLoggedIn={reportOutageForLoggedIn}
          setLoggedInUserNotificationRequestAccountParams={
            setLoggedInUserNotificationRequestAccountParams
          }
        />
      </div>
    </Modal>
  );
};

export default OutageReportPopup;
